// Saved-order line attribution — the web port of Android's `OrderDetailModel.kt` helpers
// (`isDeziDiscounted` · `deziLineDiscountTotal` · `deziDiscountBreakdown`) plus the row builder the
// order-history screen renders.
//
// WHY THIS IS A MODULE AND NOT A CLOSURE IN THE MODAL: the amounts here decide what a cashier
// believes came off a line before they refund it, and the two scales in play are easy to confuse —
// `discount_amt` is PER UNIT while `altria_discount_desc.mkonnekt.discount` is a LINE TOTAL. Android
// centralised them for the same reason (three adapters were drifting). Pure and unit-tested.

import { roundHalfUp, toNum } from "./money";
import { opCompanyLabel } from "../../../services/mkonnekt/opCompany";

/**
 * The echoed `altria_discount_desc.mkonnekt` node, parse-safe at BOTH levels.
 *
 * The register writes `altria_discount_desc` with `JSON.stringify`, and the order endpoints hand it
 * back either as that string or as a decoded object depending on the call — so each level is parsed
 * only when it is still a string. Anything unparseable degrades to `null` (⇒ no attribution) rather
 * than throwing inside a render.
 */
export function mkonnektOf(line) {
  const parse = (v) => {
    if (typeof v !== "string") return v;
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  };
  const desc = parse(line && line.altria_discount_desc);
  return parse(desc && desc.mkonnekt) || null;
}

/**
 * The manufacturer-discount LINE TOTAL echoed on the mkonnekt node, floored at 0.
 *
 * ⚠️ ALREADY × QUANTITY. Never multiply it by qty the way the per-unit `discount_amt` path does: a
 * qty-2 line carrying a $3.80 line total would then report $7.60. Every real order seen so far is
 * qty 1, where the two are indistinguishable — which is exactly how that ships wrong.
 */
export function deziLineDiscountTotal(line) {
  return Math.max(toNum((mkonnektOf(line) || {}).discount), 0);
}

/**
 * True when this saved line carries a manufacturer discount. Absence-safe: a missing or zeroed node
 * (non-dezi lines echo a zeroed one) reads false, so a plain line never sprouts a "(−$0.00)" row.
 */
export function isDeziDiscounted(line) {
  return deziLineDiscountTotal(line) > 0;
}

/**
 * The individual Altria allowances that add up to `deziLineDiscountTotal`, in wire order.
 * Each `amount` is THIS line's share, already × quantity — the same scale as the total, so it is
 * shown as-is too. Entries with no name or a non-positive amount have nothing to show and are
 * dropped rather than rendered as a bare row. Each row also carries the allowance's `id`, `itemUPC`,
 * `discountType`, `opCompanyCode` and the manufacturer's display label `manufacturer` ("" when absent).
 */
export function deziDiscountBreakdown(line) {
  const mk = mkonnektOf(line);
  const raw = mk && Array.isArray(mk.breakdown) ? mk.breakdown : [];
  const out = [];
  for (const b of raw) {
    const name = String((b && b.name) || "").trim();
    const amount = toNum(b && b.amount);
    if (!name || amount <= 0) continue;
    const opCompanyCode = String((b && b.opCompanyCode) ?? "").trim();
    out.push({
      id: String((b && b.id) ?? "").trim(),
      itemUPC: String((b && b.itemUPC) ?? "").trim(),
      name,
      amount,
      discountType: String((b && b.discountType) ?? "").trim(),
      opCompanyCode,
      manufacturer: opCompanyLabel(opCompanyCode),
    });
  }
  return out;
}

/**
 * The allowance breakdown to DISPLAY for each saved line of one order, index-aligned with `lines`.
 *
 * Two writers produce different shapes, and both must render without inflating anything:
 *  • The web/Android register splits every allowance across a UPC's lines, so each line's breakdown
 *    is ITS OWN SHARE (Σ breakdown === that line's `mkonnekt.discount`). Shown per line as-is.
 *  • The online storefront splits only the numeric `discount` and copies the UPC's WHOLE breakdown
 *    onto every line of that UPC (Σ breakdown > the line's discount). Showing each copy would
 *    multiply the allowance by the number of unit rows, so a copy is shown ONCE — on the first line
 *    carrying it — de-duplicated by allowance id + itemUPC (the same key the Node backend uses when
 *    it rebuilds the ticket for mKonnekt).
 * Display only: no total anywhere is derived from this.
 */
export function deziBreakdownForLines(lines) {
  const rows = Array.isArray(lines) ? lines : [];
  const seen = new Set();
  return rows.map((line) => {
    const entries = deziDiscountBreakdown(line);
    if (entries.length === 0) return [];
    const entriesTotal = entries.reduce((sum, b) => sum + b.amount, 0);
    // A cent of rounding per entry is tolerated before a breakdown is treated as a copy.
    const isCopy = entriesTotal - deziLineDiscountTotal(line) > 0.01 * entries.length + 1e-9;
    if (!isCopy) return entries;
    const lineUpc = String((line && line.upc) || "").trim();
    const out = [];
    for (const b of entries) {
      const key = `${b.id || `${b.name}|${b.discountType}`}::${b.itemUPC || lineUpc}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(b);
    }
    return out;
  });
}

/**
 * What came off this saved line, as display rows — so the operator can see WHY a row refunds what it
 * does. Returns `[{ label, amount, sub? }]`; `sub` marks a per-allowance breakdown line.
 *
 * Branch order mirrors Android's adapter exactly:
 *  1. `discount_amt > 0` — the IN-STORE path. Per-unit, so the amount shown is `discount_amt × qty`.
 *     The mkonnekt node is a PRESENCE CHECK ONLY here (it decides the label), because manual
 *     discounts and item coupons are blocked on a dezi line — a line's `discount_amt` is therefore
 *     either all-manual or all-manufacturer, never a mix.
 *  2. else mkonnekt total > 0 — the ONLINE path. The online-ordering backend applies the discount by
 *     REDUCING the line price (catalog $100.00 → saved price $98.10) and leaves `discount_amt` at 0,
 *     so without this branch the money came off the order with NO label at all and the operator
 *     could not tell a manufacturer discount from a mispriced item. LABEL-ONLY: nothing is
 *     subtracted anywhere, because the money is already inside the saved line price.
 *  3. an item coupon is additive — it coexists with either branch above.
 */
export function lineDiscountRows(line) {
  const rows = [];
  const qty = toNum(line && line.qty) || 1;
  const perUnit = toNum(line && line.discount_amt);
  const deziTotal = deziLineDiscountTotal(line);

  // Android InStoreOrderItemAdapterNew: an EXCLUSIVE chain — a manual/manufacturer discount, else a
  // BOGO deal, else a Mix N' Match deal, else a bare dezi row. bogo_discount / mix_match_amt are
  // PER-UNIT on the returned cart_data (the adapter multiplies by qty), matching discount_amt above.
  const bogo = toNum(line && line.bogo_discount);
  const mixMatch = Math.abs(toNum(line && line.mix_match_amt));
  if (perUnit > 0) {
    rows.push({ label: deziTotal > 0 ? "Manufacturer Discount" : "Item Discount", amount: perUnit * qty });
  } else if (bogo > 0) {
    rows.push({ label: "BOGO Deal", amount: bogo * qty });
  } else if (mixMatch > 0) {
    rows.push({ label: "Mix N' Match Deal", amount: mixMatch * qty });
  } else if (deziTotal > 0) {
    rows.push({ label: "Manufacturer Discount", amount: deziTotal });
    for (const b of deziDiscountBreakdown(line)) {
      rows.push({
        label: b.name,
        amount: b.amount,
        sub: true,
        discountType: b.discountType,
        opCompanyCode: b.opCompanyCode,
        manufacturer: b.manufacturer,
      });
    }
  }

  const couponJson = line && line.item_coupon_json;
  const coupon = toNum(couponJson && couponJson.discount_amount);
  if (coupon > 0) {
    const code = String((couponJson && couponJson.coupon_code) || "").trim();
    rows.push({ label: code ? `Coupon(${code})` : "Coupon", amount: coupon * qty });
  }
  return rows;
}

// Order-summary discount rollup — Android InStoreOrderDetailFragmentNewVersion.
// `Discount` total = bogo_discount + mix_match_discount (from the order loyalty JSON) + Σ line
// coupon_code_amt, but the coupon part is only ADDED when the order is an OTD order (coupon_code
// contains "OTD"); otherwise a coupon shows as its own "Coupon (name)" sub-row and a custom % / $
// discount shows as a "Custom Discount" sub-row (Σ line custom_discount_amt).
// Returns { total, coupon: {label, amount} | null, custom: amount | null } — total 0 ⇒ render nothing.
export function orderDiscountSummary(orderDetail, cart, loyaltyJson) {
  const lj = loyaltyJson || {};
  const rows = Array.isArray(cart) ? cart : [];
  const bogo = toNum(lj.bogo_discount);
  const mixMatch = toNum(lj.mix_match_discount);
  const couponAmt = rows.reduce((s, l) => s + toNum(l && l.coupon_code_amt), 0);
  const customAmt = rows.reduce((s, l) => s + toNum(l && l.custom_discount_amt), 0);
  const hasOtd = String(lj.coupon_code || "").toUpperCase().includes("OTD");
  const total = bogo + mixMatch + (hasOtd ? couponAmt : 0);
  let coupon = null;
  if (couponAmt > 0 && !hasOtd) {
    const name = String(lj.coupon_code || "").trim();
    coupon = { label: name ? `Coupon (${name})` : "Coupons", amount: couponAmt };
  }
  const custom = customAmt > 0 ? customAmt : null;
  return { discount: total, coupon, custom };
}

/**
 * Coupon $ on completed Order Summary.
 *
 * Android `InStoreOrderDetailFragmentNewVersion` always does
 * `Σ cartData.couponCodeAmt` (tender-priced `item_ses.coupon_code_amt`). Dual Price
 * cash save writes the BASE there (e.g. $3.88 at 3%), while `coupon_code` JSON
 * `coupon_code_amt` can stay CREDIT (e.g. $4.00) — nca.md getLoyaltyJson vs toParseJson.
 *
 * Prefer the cart sum (Android). Dual Price never falls back to the credit JSON
 * (cart 0 stays 0). NCA / off fall back to loyalty JSON only when cart sum is 0
 * (older / non-Android orders that only stamped the JSON).
 */
export function couponAmountForSummary(isSurcharge, couponDetails, cartData) {
  const rows = Array.isArray(cartData) ? cartData : [];
  const fromCart = rows.reduce((s, l) => s + toNum(l && l.coupon_code_amt), 0);
  if (isDualPriceMode(isSurcharge)) return fromCart;
  if (fromCart > 0) return fromCart;
  return toNum(couponDetails && couponDetails.coupon_code_amt);
}

/**
 * Custom Discount $ on completed Order Summary.
 * Android always sums `cartData.customDiscountAmt` (same tender-priced save path as coupon).
 */
export function customDiscountAmountForSummary(cartData) {
  const rows = Array.isArray(cartData) ? cartData : [];
  return rows.reduce((s, l) => s + toNum(l && l.custom_discount_amt), 0);
}

/**
 * Android `tvOrderDiscountValue` (Order Summary Discount row):
 * `bogo + mix_match` (+ Σ coupon only when `coupon_code` contains `"OTD"`).
 * Custom Discount is a **separate** row — never subtract it here.
 * (Old React path did `… − custom` when `coupon_code === "Discount"`, which went negative and
 * rendered as `-$-14.00`.) Floored at 0 for safe `-$${amt}` formatting.
 */
export function discountAmountForSummary(isSurcharge, couponDetails, cartData) {
  const bogo = toNum(couponDetails && couponDetails.bogo_discount);
  const mix = toNum(couponDetails && couponDetails.mix_match_discount);
  const code = String((couponDetails && couponDetails.coupon_code) || "");
  const hasOTD = code.toUpperCase().includes("OTD");
  const coupon = hasOTD
    ? couponAmountForSummary(isSurcharge, couponDetails, cartData)
    : 0;
  return Math.max(0, bogo + mix + coupon);
}

// ── Order-summary "Taxes" block — Android InStoreOrderDetailFragmentNewVersion + SummaryTaxAdapter ──
// Default tax row : name = store tax list's DefaultTax display name (else "Default Tax"),
//                   taxable = Σ over lines with default_tax_amount > 0 of (price − discount_amt −
//                   item-coupon discount) × (qty + refund_qty), rate = order tax_rate, due = order tax.
// Other tax rows  : one per key of order_detail.other_taxes_desc {name: amount}; taxable = same sum over
//                   the lines whose other_taxes_desc names that tax; rate = first such line's
//                   other_taxes_rate_desc[name] (else 0); due = the amount.
// Rates print with THREE decimals ("7.255%", "9.750%" — Android toAmount "%.3f"), total = tax + other_taxes.
function odJson(v) {
  if (v == null || v === "") return {};
  if (typeof v === "object") return v;
  try {
    const o = JSON.parse(v);
    return o && typeof o === "object" ? o : {};
  } catch {
    return {};
  }
}
function taxableOfLine(line) {
  const coupon = odJson(line && line.item_coupon_json);
  const unitNet = toNum(line && line.price) - toNum(line && line.discount_amt) - toNum(coupon.discount_amount);
  return unitNet * (toNum(line && line.qty) + toNum(line && line.refund_qty));
}
export const fmtTaxRate = (r) => (Number(toNum(r)) || 0).toFixed(3) + "%";
export function orderTaxSummary(orderDetail, cartData, defaultTaxName) {
  const od = orderDetail || {};
  const lines = Array.isArray(cartData) ? cartData : [];
  const rows = [];
  rows.push({
    name: defaultTaxName || "Default Tax",
    taxable: lines.filter((l) => toNum(l.default_tax_amount) > 0).reduce((s, l) => s + taxableOfLine(l), 0),
    rate: fmtTaxRate(od.tax_rate),
    due: toNum(od.tax),
  });
  const others = odJson(od.other_taxes_desc);
  for (const name of Object.keys(others)) {
    const withTax = lines.filter((l) => Object.prototype.hasOwnProperty.call(odJson(l.other_taxes_desc), name));
    const rateLine = lines.find((l) => Object.prototype.hasOwnProperty.call(odJson(l.other_taxes_rate_desc), name));
    rows.push({
      name,
      taxable: withTax.reduce((s, l) => s + taxableOfLine(l), 0),
      rate: fmtTaxRate(rateLine ? odJson(rateLine.other_taxes_rate_desc)[name] : 0),
      due: toNum(others[name]),
    });
  }
  return { rows, total: toNum(od.tax) + toNum(od.other_taxes) };
}

// ── Dual Price (is_surcharge == "2") — Android InStoreOrderDetailFragmentNewVersion ──
// Store setting: "0" off, "1" NCA add-on fee, "2" dual price (NCA baked into the credit price).
// On the completed-order summary Android (`ncaLineVisibility`, 2026-09-03 §23/§28):
//   • hides Credit AND Debit NCA fee rows (`View.GONE`)
//   • Dual Price Other Fees uses `dualNcaRenderFee` (`68688811` / §49): the gap between
//     charged `amt − tax` and the item-side net. 0 when the payload is already consistent.
//     React Net is already Gross − discounts, so do not also subtract that gap from Net.
//   • does not subtract cash_discounting from Net Sale on single-tender dual
//   • subtracts per-line `item_cash_pay_discount` from the item total (0 for credit / non-dual)
//   • cash-tender paid amount floors at $0 (lottery/scratcher Dual Price payout)
//   • Loyalty Points Redeemed always shows stored `loyalty_point_amt_spent` (`1aa1b12` flat LP; `7afb417` inflation removed)
// Dual price is display/attribution only on this screen — saved amt/subtotal/tax are not recomputed.

/**
 * True when the merchant's / order's `is_surcharge` is dual-price mode ("2").
 * Null / blank / whitespace / "0" / "1" / "2.0" / garbage → false (no Dual Price UI).
 */
export function isDualPriceMode(isSurcharge) {
  return String(isSurcharge ?? "").trim() === "2";
}

/**
 * Android `dualPriceModeFromOrder` / §74a (2026-09-11 `8885312`).
 * Reads `is_surcharge` stamped on the order's `coupon_code` JSON (LoyaltyDataClass).
 * Returns `"0"` | `"1"` | `"2"` when present, else `null` (order predates the key / unparseable).
 */
export function surchargeModeFromOrder(orderDetail, couponDetails) {
  let mode;
  if (couponDetails && typeof couponDetails === "object" && !Array.isArray(couponDetails)) {
    mode = couponDetails.is_surcharge;
  }
  if (mode == null || mode === "") {
    const raw = orderDetail && orderDetail.coupon_code;
    if (raw == null || raw === "") return null;
    if (typeof raw === "object") {
      mode = raw.is_surcharge;
    } else {
      try {
        mode = JSON.parse(raw)?.is_surcharge;
      } catch {
        return null;
      }
    }
  }
  if (mode == null || String(mode).trim() === "") return null;
  return String(mode).trim();
}

/**
 * Android Order Summary / refund screens (§74a): Dual Price mode comes from the ORDER only.
 * Missing stamp (older orders) → `"0"` (not Dual Price). Do **not** fall back to the live store setting.
 */
export function resolveIsSurchargeForSummary(orderDetail, couponDetails) {
  const mode = surchargeModeFromOrder(orderDetail, couponDetails);
  return mode != null ? mode : "0";
}

/**
 * Android `tvTendersTotalValue`:
 * `amt + gift_card_amount + cash_back_amt + cash_back_fee`.
 * Lottery / scratcher / store credit are already inside saved `amt` (Cash row subtracts them for display).
 */
export function tendersTotalForSummary({
  amt,
  giftCardAmount,
  cashBackAmt,
  cashBackFee,
} = {}) {
  return (
    toNum(amt) + toNum(giftCardAmount) + toNum(cashBackAmt) + toNum(cashBackFee)
  );
}

/**
 * Loyalty Points Redeemed dollars on Order Summary / print / email.
 *
 * Android `1aa1b12` (2026-09-08) replaced the `7afb417` credit inflation with a flat
 * loyalty model: both cash and credit show the stored `loyalty_point_amt_spent`.
 * Do not multiply by `cash_discounting_percentage`. Net Sale subtracts the same figure.
 */
export function creditLoyaltySpend(_isSurcharge, _orderDetail, loyaltyAmountSpent) {
  return toNum(loyaltyAmountSpent);
}

/**
 * Cash dual-price orders persist `cash_pay_discount` / `item_cash_pay_discount`.
 * Credit dual-price orders store 0 there (NCA is informational on `cash_discounting`) — those
 * still need `isDualPriceMode(is_surcharge)` to hide the NCA line.
 */
export function orderHasCashPayDiscount(orderDetail, cartData) {
  if (toNum(orderDetail && orderDetail.cash_pay_discount) > 0) return true;
  const rows = Array.isArray(cartData) ? cartData : [];
  return rows.some((line) => toNum(line && line.item_cash_pay_discount) > 0);
}

/** Online orders (`is_online === "1"`) — Dual Price / in-store NCA Other Fees do not apply. */
export function isOnlineOrder(orderDetail) {
  return String(orderDetail?.is_online ?? "").trim() === "1";
}

/**
 * Hide Credit AND Debit NCA fee rows on Order Summary (Android ncaLineVisibility).
 * Also hide for online orders — NCA / Dual Price Other Fees are in-store Order Summary only.
 */
export function shouldHideNcaOnSummary(isSurcharge, orderDetail) {
  if (orderDetail != null && isOnlineOrder(orderDetail)) return true;
  return isDualPriceMode(isSurcharge);
}

function lineCouponDiscount(line) {
  const raw = line && (line.item_coupon_json ?? line.itemCouponJson);
  if (raw == null || raw === "") return 0;
  if (typeof raw === "object") return toNum(raw.discount_amount ?? raw.discountAmount);
  try {
    const parsed = JSON.parse(raw);
    return toNum(parsed && (parsed.discount_amount ?? parsed.discountAmount));
  } catch {
    return 0;
  }
}

/**
 * Item-side net used by Android `dualNcaRenderFee` (`68688811`, nca.md §49).
 * Same expression as Taxable Amt, without the tax filter, so non-taxable lines still count.
 */
export function itemNetForNca(cartData) {
  const rows = Array.isArray(cartData) ? cartData : [];
  return rows.reduce((sum, line) => {
    const unitNet =
      toNum(line && line.price) -
      toNum(line && (line.discount_amt ?? line.discountAmt)) -
      lineCouponDiscount(line);
    const qty =
      toNum(line && line.qty) + toNum(line && (line.refund_qty ?? line.refundQty));
    return sum + unitNet * qty;
  }, 0);
}

/**
 * Dual Price NCA correction derived from the saved payload (`68688811` / §49).
 * `amt − tax − tip − itemNet` is the NCA sitting in a blend-space total against base-space
 * item rows. A self-consistent payload (single tender, blend-on split) is exactly 0.
 * Replaces the earlier Σ split `cash_discounting_amount` rule (`bd91d32b4b`), which
 * double-counted a fully blended split.
 *
 * React Net Sale is already Gross − discounts, so do not subtract this from Net again.
 * Put the same amount on Other Fees so Order Total still equals the charged amount.
 */
export function dualNcaRenderFee(isSurcharge, orderDetail, cartData, giftCardAmount) {
  if (!isDualPriceMode(isSurcharge)) return 0;
  const od = orderDetail || {};
  // Online Order Summary never shows Dual Price NCA (e.g. 6AA025EF42392: con_fee was
  // misread as a $0.01 Non-Cash Adjustment Fee). Android Other Fees = del/con/tip only.
  if (isOnlineOrder(od)) return 0;
  const charged =
    toNum(od.amt) +
    toNum(giftCardAmount) -
    (toNum(od.tax) + toNum(od.other_taxes ?? od.otherTaxes)) -
    (toNum(od.tip) + toNum(od.tip_refund_amount ?? od.tipRefundAmount)) -
    // del/con are already Other Fees rows — do not treat them as an NCA gap
    toNum(od.del_fee ?? od.delFee) -
    toNum(od.con_fee ?? od.conFee);
  const fee = charged - itemNetForNca(cartData);
  return fee > 0 ? roundHalfUp(fee) : 0;
}

/**
 * Android otherFeeNca.
 * Online → always 0 (no Non-Cash Adjustment Fee on online Order Summary).
 * Dual Price → `dualNcaRenderFee` (0 when the payload is already consistent).
 * Else the summed NCA amount.
 *
 * `context` is `{ orderDetail, cartData, giftCardAmount }`.
 */
export function ncaAmountForOtherFees(isSurcharge, ncaTotal, context) {
  const ctx = context && !Array.isArray(context) ? context : {};
  if (isOnlineOrder(ctx.orderDetail)) return 0;
  if (isDualPriceMode(isSurcharge)) {
    return dualNcaRenderFee(
      isSurcharge,
      ctx.orderDetail,
      ctx.cartData,
      ctx.giftCardAmount,
    );
  }
  const n = toNum(ncaTotal);
  return n > 0 ? n : 0;
}

/**
 * Android `d8abad2` (2026-09-04): Dual Price refund list folds refunded NCA into the CARD amount
 * (`credit_amt + nca_amt`). The NCA fee row / Tip+NCA suffix stay hidden, so without this fold the
 * surcharge the customer got back vanishes from every visible refund figure.
 *
 * NCA mode ("1") / off: return credit only (NCA stays a separate total component + Tip+NCA text).
 */
export function creditRefundAmountForSummary(isSurcharge, creditAmt, ncaAmt) {
  const credit = toNum(creditAmt);
  if (isDualPriceMode(isSurcharge)) return credit + toNum(ncaAmt);
  return credit;
}

/**
 * Separate NCA add-on for Total Refund.
 * Dual Price (`d8abad2`): always 0 here — NCA is already inside `creditRefundAmountForSummary`.
 * Adding it again would double-count. NCA mode ("1") / off keep refunded NCA as its own component.
 *
 * (Place-time Android `d74fb1c` / `c5d1dc9` touch InStoreRefundRepository payloads, not this display.)
 */
export function ncaAmountForRefundTotal(isSurcharge, ncaRefund) {
  if (isDualPriceMode(isSurcharge)) return 0;
  const n = toNum(ncaRefund);
  return n > 0 ? n : 0;
}

/**
 * Payment-side refund $ for one `refund_data` row (excludes tip).
 * Dual Price credit = credit+nca; other tenders unchanged.
 */
export function refundPaymentAmountForSummary(isSurcharge, refund) {
  if (!refund) return 0;
  const paymentTypeSum =
    creditRefundAmountForSummary(isSurcharge, refund.credit_amt, refund.nca_amt) +
    toNum(refund.cash_amt) +
    toNum(refund.debit_amt) +
    toNum(refund.store_credit_amt) +
    toNum(refund.giftcard_amt);

  if (paymentTypeSum > 0) return paymentTypeSum;

  if (refund.amount != null && refund.amount !== "") {
    // Stored `amount` is already base+nca+tip on Dual Price; strip tip (and NCA only in non-dual).
    return (
      toNum(refund.amount) -
      toNum(refund.tip_amt) -
      (isDualPriceMode(isSurcharge) ? 0 : toNum(refund.nca_amt))
    );
  }

  return 0;
}

/** Cash tender never shows the Tip/NCA parenthetical. Dual Price also hides the NCA part. */
export function shouldShowRefundTipAndNca(isSurcharge, isCashMethod, tipAmt, ncaAmt) {
  if (isCashMethod) return false;
  if (toNum(tipAmt) > 0) return true;
  return ncaAmountForRefundTotal(isSurcharge, ncaAmt) > 0;
}

/** NCA mode keeps `Tip + NCA`. Dual Price drops NCA from the string (tip-only, or empty). */
export function formatRefundTipAndNcaText(isSurcharge, tipAmt, ncaAmt) {
  const tip = toNum(tipAmt);
  const nca = ncaAmountForRefundTotal(isSurcharge, ncaAmt);
  if (isDualPriceMode(isSurcharge)) {
    return tip > 0 ? `(Tip:-$${tip.toFixed(2)})` : "";
  }
  return `(Tip:-$${tip.toFixed(2)} NCA:-$${toNum(ncaAmt).toFixed(2)})`;
}

/**
 * Cash-side tender before the Dual Price floor.
 * `amt (+ cash-back) − storeCredit − lottery − scratcher` — may be negative when lottery/scratcher
 * payout exceeds the cash order total (common Dual Price cash + lottery).
 */
export function cashTenderSideAmount({
  amt,
  cashBackAmt,
  cashBackFee,
  storeCreditSpent,
  lotteryPay,
  scratchPay,
  isOnline,
} = {}) {
  let payment = toNum(amt);
  if (toNum(cashBackAmt) > 0) payment += toNum(cashBackAmt);
  if (toNum(cashBackFee) > 0) payment += toNum(cashBackFee);
  if (toNum(storeCreditSpent) > 0) payment -= toNum(storeCreditSpent);
  if (!isOnline && toNum(lotteryPay) > 0) payment -= toNum(lotteryPay);
  if (!isOnline && toNum(scratchPay) > 0) payment -= toNum(scratchPay);
  return payment;
}

/**
 * Android `tvTenderCashAmt` / print Cash Paid (2026-09-03 `fb33a72`):
 * cashTenderSideAmount, then `coerceAtLeast(0)`.
 * Dual Price cash + lottery/scratcher can make the cash side negative; do not display `abs()` as cash paid.
 * Cash-back amt/fee stay in the React formula (they were already there).
 *
 * Android visibility: show the Cash tender row when `abs(raw) > 0`, value = floored (so −$3 raw → show −$0.00).
 */
export function cashTenderPaidAmount(opts = {}) {
  return Math.max(cashTenderSideAmount(opts), 0);
}

/** True when Android would show the Cash tender line (`abs(raw) > 0`). */
export function shouldShowCashTenderRow(opts = {}) {
  return Math.abs(cashTenderSideAmount(opts)) > 0;
}

/**
 * Plain cash tender — Android `paymentId == CASH` and not Cash/Food EBT.
 * A fully discounted Dual Price cash order (`amt` 0, no lottery) still lists
 * Tenders → Cash −$0.00. Lottery overpay uses `shouldShowCashTenderRow` (raw ≠ 0, display floored).
 */
export function isPlainCashPayment(paymentId, cardType) {
  const id = String(paymentId ?? "").trim().toLowerCase();
  const ct = String(cardType ?? "").trim().toLowerCase();
  if (ct.includes("ebt")) return false;
  return id === "cash" || ct === "cash";
}

/**
 * Android `refundNcaBaseFactor` (2026-09-03 `30f26c4`, tip-refund `1614585`).
 * Dual Price credit→base factor for refund item selection. NCA is goods-only — never on tip or
 * del/con fees — so the denominator is
 * `amt − (tip + tip_refund_amount) − del_fee − con_fee − (con_fee × tax_rate/100)`.
 * Do NOT simplify to `1 − nca/amt` (that is the bug; invisible without a tip).
 * Non-dual / nca<=0 / goodsTotal<=0 → 1.0.
 */
export function refundNcaBaseFactor(isSurcharge, orderDetail) {
  if (!isDualPriceMode(isSurcharge)) return 1;
  const od = orderDetail || {};
  // Android 38186cb: remaining NCA plus already-refunded NCA, so a later refund
  // still uses the original goods factor after a partial NCA refund.
  const nca =
    toNum(od.cash_discounting) +
    toNum(
      od.cashdiscount_refund_amount ??
        od.cash_discount_refund_amount ??
        od.cashDiscountRefundAmount,
    );
  const conFee = toNum(od.con_fee);
  const tipTotal =
    toNum(od.tip) + toNum(od.tip_refund_amount ?? od.tipRefundAmount);
  const goodsTotal =
    toNum(od.amt) -
    tipTotal -
    toNum(od.del_fee) -
    conFee -
    conFee * (toNum(od.tax_rate) / 100);
  if (!(nca > 0) || !(goodsTotal > 0)) return 1;
  return 1 - nca / goodsTotal;
}

/**
 * `item_cash_pay_discount` is a WHOLE-LINE amount (already × original qty), not per-unit.
 * Share it across a refunded vs remaining display row by displayQty / (qty + refund_qty).
 * Missing/null/zero/invalid/≤0 → 0 (credit, NCA mode, surcharge-off) — no extra Dual Price UI.
 *
 * Android nca.md §20 (2026-09-03): consume the STORED per-line value as-is. Do not re-weight
 * order-level `cash_pay_discount` by row NET. One-shot cash Dual Price lines are LIST-weighted
 * so `creditList − baseDeal − item_cash_pay_discount` = cash net (BOGO freebie → $0.00).
 */
export function cashPayDiscountForQty(line, displayQty) {
  const disc = Math.max(toNum(line && line.item_cash_pay_discount), 0);
  if (!(disc > 0)) return 0;
  const rawQty = toNum(line && (line.qty ?? line.quantity));
  const refundQty = toNum(line && line.refund_qty);
  const totalQty = rawQty + refundQty;
  const shown = toNum(displayQty);
  if (!(totalQty > 0) || !(shown > 0)) return 0;
  return disc * (shown / totalQty);
}

/**
 * Android InStoreOrderDetailFragmentNewVersion order summary (register Orders → detail), from the SAVED
 * order: Gross Sale = subtotal; Loyalty Redeemed = −loyalty_point_amt_spent; Net Sale =
 * |amt + GC − (tax + other) − (tip + tipRef) − (dual ? 0 : NCA + NCA refunded)|; Other Fees =
 * (dual ? NCA gap : NCA + NCA refunded) + cash_back_fee + tip + tipRef; Grand Total = cash_back_fee + |amt| + GC;
 * refunds: Total Tax Refunded = Σ row default/other tax refunded; Grand Total After Refund =
 * Grand Total − Σ(refund amount − refunded loyalty $), ≤ 1¢ shown as 0.
 */
export function registerOrderSummary(orderDetail, cartData = [], refundData = [], isSurcharge, loyaltyJson = {}) {
  const od = orderDetail || {};
  const loy = loyaltyJson || {};
  const dual = isDualPriceMode(isSurcharge);
  const gc = toNum(loy.gift_card_amount);
  const tipAll = toNum(od.tip) + toNum(od.tip_refund_amount ?? od.tipRefundAmount);
  const ncaAll = toNum(od.cash_discounting) + toNum(od.cashdiscount_refund_amount ?? od.cashDiscountRefundAmount);
  const tax = toNum(od.tax) + toNum(od.other_taxes ?? od.otherTaxes);
  const netSale = Math.abs(toNum(od.amt) + gc - tax - tipAll - (dual ? 0 : ncaAll));
  const otherFeeNca = dual ? dualNcaRenderFee(isSurcharge, od, cartData, gc) : ncaAll;
  const grandTotal = toNum(od.cash_back_fee ?? od.cashBackFee) + Math.abs(toNum(od.amt)) + gc;
  const rows = Array.isArray(cartData) ? cartData : [];
  const totalTaxRefunded = rows.reduce((s, r) => s + toNum(r && r.default_tax_refund_amount) + toNum(r && r.other_taxes_refund_amount), 0);
  const refunds = Array.isArray(refundData) ? refundData : [];
  const totalRefund = refunds.reduce((s, r) => s + toNum(r && r.amount), 0);
  const refundedNoLp = refunds.reduce((s, r) => s + toNum(r && r.amount) - toNum(r && r.loyalty_point_amt), 0);
  let afterRefund = roundHalfUp(grandTotal - refundedNoLp);
  if (afterRefund <= 0.01) afterRefund = 0;
  return {
    grossSale: roundHalfUp(toNum(od.subtotal)),
    loyaltyRedeemedPoints: toNum(loy.loyalty_point_spent),
    loyaltyRedeemed: roundHalfUp(toNum(loy.loyalty_point_amt_spent)),
    netSale: roundHalfUp(netSale),
    totalTax: roundHalfUp(tax),
    otherFees: roundHalfUp(otherFeeNca + toNum(od.cash_back_fee ?? od.cashBackFee) + tipAll),
    grandTotal: roundHalfUp(grandTotal),
    changeDue: roundHalfUp(toNum(od.change_due ?? od.changeDue)),
    totalTaxRefunded: roundHalfUp(totalTaxRefunded),
    totalRefund: roundHalfUp(totalRefund),
    grandTotalAfterRefund: afterRefund,
  };
}
